import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Node } from "@iventis/domain-model/model/node";
import { FROM_MAP_ENGINE, machineEventTypes } from "@iventis/map-engine";
import { findNode, findNodesThatAreAncestors, listOfAllDescendants, isOnlyChildInSelection, getAllAncestorsToSelect, findNodes } from "@iventis/tree-browser";
import { defaultTreeBrowserSelection, TreeBrowserSelection, TreeBrowserSelectionPayload } from "@iventis/tree-browser/state/tree-browser-selection";
import { RoutePlannerEvent, routePlannerEventStream } from "@iventis/route-planner";
import { TimeSpan } from "@iventis/types";
import { ZERO_TIME_SPAN } from "@iventis/utilities";
import { getMapObjectRouteTimeSystemDataField, getMapObjectLengthSystemDataField } from "@iventis/datafields";
import { map } from "./state/map.slice";
import { dispatch, rootStore } from "./state/root.store";
import { eventStream } from "./event-stream";
import { mapDataTableEventStream, MapDataTableEvent } from "./data-table/sidebar-data-table-event-stream";
import { FROM_MAP_ROOT, FROM_MAP_UNDO_REDO } from "./map-constants";
import { FROM_DATA_TABLE } from "./data-table/sidebar-data-table";

export const mapTreeBrowserSelection: TreeBrowserSelection<Node> = {
    fromSelectNodes: (payload: TreeBrowserSelectionPayload<Node, "select">) => {
        const { selectedNodeIds, nodes, ctrlHeld, ignoreExistingSelection, from, affectedNodeTypes, undoable } = {
            ctrlHeld: false,
            ...payload,
            // If undoable is undefined, default to true
            undoable: payload.undoable ?? true,
        };
        const mainNode = findNode([payload.tree], payload.mainNodeId);
        // If we are ignoring selection, then we assume no selected ancestors
        const selectedAncestors = ignoreExistingSelection
            ? []
            : // We need to know which of the selected nodes are ancestors of the toggled nodes (recursive parents)
              findNodesThatAreAncestors(
                  mainNode,
                  nodes.map((n) => (typeof n === "string" ? n : n.id)),
                  selectedNodeIds
              );
        let resultingSelection = [...nodes].reduce(
            (selectedNodes, node) => {
                // Get the full node (the node param can either being string or Node)
                const fullNode: Node = typeof node === "string" ? findNode([mainNode], node) ?? ({ id: node } as Node) : node;

                // If we are deselecting, remove the node, all it's descendants and all its selected ancestors
                if (
                    ((!ignoreExistingSelection && isOnlyChildInSelection(mainNode, selectedNodeIds, [], fullNode.id)) || ctrlHeld) &&
                    selectedNodes.some((n) => n.id === fullNode.id)
                ) {
                    return selectedNodes.filter(
                        (n) =>
                            !(
                                n.id === fullNode.id || // Filter out the node
                                // Filter out the ancestors if the node is not a map object
                                (fullNode.type !== NodeType.MapObject && selectedAncestors.includes(n.id)) ||
                                // Filter out all their descendants
                                listOfAllDescendants(fullNode).some(({ id }) => n.id === id)
                            )
                    );
                }

                // Get all the descendants of the given node ONLY IF the node being selected is NOT a layer
                const descendants = (fullNode.type === NodeType.Layer ? ([] as Node[]) : listOfAllDescendants(fullNode)).filter(
                    (node) => ![NodeType.DataFieldListItem, NodeType.MapObject].includes(node.type)
                );

                // Accumulate the ancestors required for selection, based on the current selection
                const ancestorsToSelect: Node[] = [];
                if (fullNode.type === NodeType.Layer || fullNode.type === NodeType.Folder) {
                    // Get all the ancestors which are not yet selected but need to be (i.e. if we are selecting a node which completes the full selection of a parents children)
                    // Don't select a folder if all children are selected, makes it easier to use copy group/layer functionality
                    const ancestorsWithoutFolders = getAllAncestorsToSelect(
                        mainNode,
                        fullNode,
                        selectedNodes.map((n) => n.id)
                    ).filter((n) => n.type !== NodeType.Folder);

                    ancestorsToSelect.push(...ancestorsWithoutFolders);
                } else {
                    const parent = findNode([mainNode], fullNode.parentId);
                    // If the parent is a layer, then we must select it and thus any ancestors of the layer that require selection
                    if (parent?.type === NodeType.Layer) {
                        ancestorsToSelect.push(
                            ...[
                                parent,
                                ...getAllAncestorsToSelect(
                                    mainNode,
                                    parent,
                                    selectedNodes.map((n) => n.id)
                                ),
                            ]
                        );
                    }
                }
                // Combine all descendants and ancestors that require selecting
                return [...selectedNodes.filter((n) => !(descendants.some(({ id }) => n.id === id) || n.id === fullNode.parentId)), fullNode, ...descendants, ...ancestorsToSelect];
            },
            // If we are clearing or ignoring existing selection, then start the reducer with an empty array
            !ctrlHeld || ignoreExistingSelection ? [] : findNodes(mainNode.childNodes, selectedNodeIds)
        );
        // If it's coming from the map engine then we need to persist the layer/group selection
        if (resultingSelection.length === 0 && from === FROM_MAP_ENGINE) {
            //  if affectedNodeTypes is not defined, we assume all nodes are affected
            resultingSelection = findNodes(mainNode.childNodes, selectedNodeIds)?.filter((node) => (affectedNodeTypes == null ? false : !affectedNodeTypes?.includes(node.type)));
        }

        // Run the side effects
        mapTreeBrowserSelectionSideEffects(resultingSelection, { from, undoable });

        return resultingSelection.map((n) => n.id);
    },
    fromExpandNode: (payload) => {
        const { selectedNodeIds, nodes, tree } = payload;
        const node = typeof nodes[0] === "string" ? findNode(tree?.childNodes ?? [], nodes[0]) : nodes[0];
        const resultingSelection = [...selectedNodeIds];
        // If a selected layer is expanded, then ensure the selection includes the map objects selected in the map
        if (node.type === NodeType.Layer && selectedNodeIds.includes(node.id)) {
            const selectedMapObjects = rootStore.getState().mapReducer.mapModule.mapObjectsSelected;
            const missing = selectedMapObjects.value?.filter((o) => !selectedNodeIds.includes(o.objectId));
            if (missing?.length > 0) {
                resultingSelection.push(...missing.map((o) => o.objectId));
            }
        }
        return resultingSelection;
    },
    fromCollapseNode: (payload) => payload.selectedNodeIds,
    fromMoveNodes: defaultTreeBrowserSelection<Node>().fromMoveNodes,
    fromDeleteNodes: (payload) => {
        const resultingSelection = payload.selectedNodeIds.filter((id) => {
            // If it's in the deleted node ids, filter it out
            if (payload.deletedNodeIds.includes(id)) {
                return false;
            }
            // Also filter out any nodes who's parent is being deleted
            const node = findNode([payload.tree], id);
            return !payload.deletedNodeIds.includes(node.parentId);
        });

        mapTreeBrowserSelectionSideEffects(
            resultingSelection.map((nodeId) => findNode([payload.tree], nodeId)),
            { from: payload.from ?? FROM_MAP_ROOT, undoable: false }
        );

        return resultingSelection;
    },
};

/** Update the relevant stores across the app who are interested in the selected nodes (In future, this would be one event stream) */
const mapTreeBrowserSelectionSideEffects = (resultingSelection: Node[], options: { from: string; undoable: boolean | undefined }) => {
    const { MapObject: objects, Layer: layers } = resultingSelection.reduce(
        (acc, node) => {
            acc[node.type]?.push?.(node);
            return acc;
        },
        { [NodeType.MapObject]: [] as Node[], [NodeType.Layer]: [] as Node[] }
    );
    // Layer selection needs updating
    dispatch(map.actions.updateLayerSelection({ selection: layers.map(({ id }) => id) }));

    // If the object geom exists locally and the object is a route, then we want to send the mode of transport to the route planner
    if (objects.length === 1) {
        const [object] = objects;
        const geom = rootStore.getState().mapReducer.mapModule.geoJSON?.value?.[object.parentId]?.find((g) => g.objectId === object.id);
        const isAlreadySelected = rootStore.getState().mapReducer.mapModule.mapObjectsSelected.value?.find((o) => o.objectId === object.id);
        const modeOfTransport = geom?.feature?.properties?.modeOfTransport;

        if (modeOfTransport && !isAlreadySelected) {
            // Tell the planner the mode of transport for the selected route
            routePlannerEventStream.next({ type: RoutePlannerEvent.SetModeOfTransport, payload: { modeOfTransport } });

            // Get the route info from the system data fields on the object
            const { projectDataFields } = rootStore.getState().mapReducer.mapModule;
            const durationProperty = getMapObjectRouteTimeSystemDataField(projectDataFields)?.id;
            const duration: TimeSpan = durationProperty ? (geom.feature.properties[getMapObjectRouteTimeSystemDataField(projectDataFields)?.id] as TimeSpan) : ZERO_TIME_SPAN;
            const distanceProperty = getMapObjectLengthSystemDataField(projectDataFields)?.id;
            const distance = distanceProperty ? (geom.feature.properties[getMapObjectLengthSystemDataField(projectDataFields)?.id] as number) : 0;

            // Tell the route planner the route info (duration and distance)
            routePlannerEventStream.next({ type: RoutePlannerEvent.SetRouteInfo, payload: { routeInfo: { duration, distance } } });
        }
    }

    // For linked layers, the object nodes parent id may not match the layer id, so we need to accout for that by finding by source id also
    const selectedMapObjects = objects.map((node) => ({ objectId: node.id, layerId: layers.find((l) => [l.id, l.sourceId].includes(node.parentId))?.id }));
    dispatch(map.actions.setMapObjectsSelected({ newSelection: selectedMapObjects, addToUndoStack: options.undoable && options.from !== FROM_MAP_UNDO_REDO }));
    // Anything from the map engine doesn't need to go back to the map engine, and all undo/redo actions are handled through the map engine
    if (options.from !== FROM_MAP_ENGINE && options.from !== FROM_MAP_UNDO_REDO) {
        // Map engine must be told of any map object selection changes
        eventStream.next({ type: machineEventTypes.selectObjects, payload: { selectedMapObjectIds: selectedMapObjects, from: options.from, updateStore: false } });
        if (layers?.length === 1) {
            eventStream.next({ type: machineEventTypes.selectLayer, payload: { layerId: layers[0].id } });
        }
    }
    if (options.from !== FROM_DATA_TABLE) {
        mapDataTableEventStream.next({ type: MapDataTableEvent.MapObjectsSelected, payload: selectedMapObjects });
    }
};
